import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { StatusPill } from '@/components/ui/status-pill';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const ENTITY_FIELDS = {
  loads: [
    'loadNumber',
    'pickupDate',
    'deliveryDate',
    'rate',
    'shipperName',
    'consigneeName',
    'status',
    'currency',
    'miles',
    'origin',
    'destination',
    'notes',
  ],
  parties: ['name', 'partyType', 'contactEmail', 'contactPhone', 'address'],
  vehicles: ['unitNumber', 'vin', 'make', 'model', 'year', 'avgMpg', 'status'],
  drivers: ['name', 'phone', 'email', 'payModel', 'defaultDriverPct', 'status'],
};

/** All four entity imports are unlocked. */
const ACTIVE_IMPORT_ENTITIES = new Set(['loads', 'parties', 'vehicles', 'drivers']);

const REQUIRED_FIELDS = {
  loads: ['loadNumber', 'pickupDate', 'deliveryDate', 'rate', 'shipperName', 'consigneeName'],
  parties: ['name', 'partyType'],
  vehicles: ['unitNumber'],
  drivers: ['name'],
};

const FIELD_ALIASES = {
  loads: {
    loadNumber: [/load\s*#/i, /load\s*number/i, /^load$/i, /^pro$/i, /bol\s*#/i],
    pickupDate: [/pu\s*date/i, /pickup\s*date/i, /^pickup$/i, /pick\s*up/i],
    deliveryDate: [/del\s*date/i, /delivery\s*date/i, /^delivery$/i, /drop\s*date/i],
    rate: [/^rate$/i, /linehaul/i, /amount/i, /revenue/i, /^total$/i],
    shipperName: [/shipper/i, /customer\s*name/i, /bill\s*to/i],
    consigneeName: [/consignee/i, /receiver/i, /delivery\s*name/i],
    status: [/^status$/i, /load\s*status/i, /state$/i],
    currency: [/currency/i, /curr$/i],
    miles: [/miles/i, /distance/i, /mileage/i],
    origin: [/^origin$/i, /pickup\s*city/i, /from\s*city/i, /pu\s*city/i],
    destination: [/^destination$/i, /delivery\s*city/i, /to\s*city/i, /del\s*city/i],
    notes: [/note/i, /comment/i, /remark/i],
  },
  parties: {
    name: [/^name$/i, /company/i, /party\s*name/i, /customer\s*name/i],
    partyType: [/party\s*type/i, /^type$/i, /kind/i, /role$/i],
    address: [/address/i, /street/i, /location/i],
    contactEmail: [/email/i, /e-mail/i],
    contactPhone: [/phone/i, /tel/i, /mobile/i],
  },
  vehicles: {
    unitNumber: [/unit/i, /^truck$/i, /unit\s*#/i, /truck\s*#/i],
    vin: [/vin/i],
    make: [/make/i],
    model: [/model/i],
    year: [/^year$/i],
    avgMpg: [/mpg/i, /avg\s*mpg/i],
    status: [/^status$/i, /vehicle\s*status/i],
  },
  drivers: {
    name: [/^name$/i, /driver\s*name/i, /^driver$/i],
    phone: [/phone/i, /tel/i, /mobile/i, /cell/i],
    email: [/email/i, /e-mail/i],
    payModel: [/pay\s*model/i, /pay\s*type/i, /compensation/i],
    defaultDriverPct: [/pct/i, /percent/i, /split/i, /driver\s*%/i],
    status: [/^status$/i, /driver\s*status/i],
  },
};

/** Make header names unique and non-empty so duplicate Excel/CSV columns do not crash mapping. */
function uniquifyHeaders(rawHeaders) {
  const seen = new Map();
  return (rawHeaders || []).map((h, i) => {
    let base = String(h ?? '')
      .replace(/^\uFEFF/, '')
      .trim();
    if (!base || /^__empty/i.test(base)) base = `Column_${i + 1}`;
    const key = base.toLowerCase();
    const n = (seen.get(key) || 0) + 1;
    seen.set(key, n);
    return n === 1 ? base : `${base} (${n})`;
  });
}

function cellToString(val) {
  if (Array.isArray(val)) {
    const first = val.find((v) => String(v ?? '').trim() !== '');
    return cellToString(first ?? '');
  }
  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof val === 'number' && val >= 20000 && val <= 80000) {
    const parsed = XLSX.SSF.parse_date_code(val);
    if (parsed) {
      const y = parsed.y;
      const m = String(parsed.m).padStart(2, '0');
      const d = String(parsed.d).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  return String(val ?? '').trim();
}

function detectEntityFromHeaders(hdrs) {
  const norm = hdrs.map((h) => String(h).toLowerCase().replace(/[^a-z]/g, ''));
  const has = (re) => norm.some((h) => re.test(h));
  if (has(/loadnumber|loadnum/) || (has(/pickupdate|pickup/) && has(/rate/))) return 'loads';
  if (has(/unitnumber|vin/)) return 'vehicles';
  if (has(/paymodel|defaultdriverpct/) || (has(/driver/) && has(/phone|email/))) return 'drivers';
  if (has(/^name$/) && (has(/partytype|^type$/) || has(/contactemail|contactphone/))) return 'parties';
  if (has(/^name$/) && has(/phone|email/) && !has(/party/)) return 'drivers';
  if (has(/^name$/) && !has(/load/)) return 'parties';
  return 'loads';
}

function autoMapForEntity(entity, hdrs) {
  const aliases = FIELD_ALIASES[entity] || FIELD_ALIASES.loads;
  const used = new Set();
  const auto = {};
  for (const [field, preds] of Object.entries(aliases)) {
    const hit = hdrs.find((h) => !used.has(h) && preds.some((p) => p.test(String(h))));
    if (hit) {
      auto[field] = hit;
      used.add(hit);
    } else {
      auto[field] = '';
    }
  }
  const required = REQUIRED_FIELDS[entity] || [];
  if (required[0] && !auto[required[0]] && hdrs[0]) {
    auto[required[0]] = hdrs[0];
  }
  return auto;
}

const TEMPLATES = [
  {
    label: 'Loads',
    icon: 'Package',
    entity: 'loads',
    active: true,
    file: '/templates/loads-import.csv',
    fields: [
      { name: 'loadNumber', required: true, note: 'Unique load #' },
      { name: 'pickupDate', required: true, note: 'YYYY-MM-DD' },
      { name: 'deliveryDate', required: true, note: 'YYYY-MM-DD' },
      { name: 'rate', required: true, note: 'Dollars (e.g. 2500.00)' },
      { name: 'shipperName', required: true, note: 'Match or create party' },
      { name: 'consigneeName', required: true, note: 'Match or create party' },
      { name: 'status', required: false, note: 'Default closed (historical)' },
      { name: 'currency', required: false, note: 'USD default' },
      { name: 'miles', required: false, note: 'Distance' },
      { name: 'origin', required: false, note: 'Pickup city/lane' },
      { name: 'destination', required: false, note: 'Delivery city/lane' },
      { name: 'notes', required: false, note: 'Free text' },
    ],
  },
  {
    label: 'Parties',
    icon: 'Users',
    entity: 'parties',
    active: true,
    file: '/templates/parties-import.csv',
    fields: [
      { name: 'name', required: true, note: 'Live party in Parties' },
      { name: 'partyType', required: true, note: 'shipper / consignee / both' },
      { name: 'contactEmail', required: false, note: 'Optional' },
      { name: 'contactPhone', required: false, note: 'Optional digits' },
      { name: 'address', required: false, note: 'Street / city' },
    ],
  },
  {
    label: 'Vehicles',
    icon: 'Truck',
    entity: 'vehicles',
    active: true,
    file: '/templates/vehicles-import.csv',
    fields: [
      { name: 'unitNumber', required: true, note: 'Fleet unit # (unique)' },
      { name: 'vin', required: false, note: '17-char VIN if known' },
      { name: 'make', required: false, note: 'e.g. Kenworth' },
      { name: 'model', required: false, note: 'e.g. T680' },
      { name: 'year', required: false, note: 'Model year' },
      { name: 'avgMpg', required: false, note: 'Average MPG' },
      { name: 'status', required: false, note: 'Default active (live fleet)' },
    ],
  },
  {
    label: 'Drivers',
    icon: 'UserRound',
    entity: 'drivers',
    active: true,
    file: '/templates/drivers-import.csv',
    fields: [
      { name: 'name', required: true, note: 'Driver full name (unique)' },
      { name: 'phone', required: false, note: 'Optional digits' },
      { name: 'email', required: false, note: 'Optional' },
      { name: 'payModel', required: false, note: 'percent / mileage / salary / owner_op' },
      { name: 'defaultDriverPct', required: false, note: 'e.g. 72 or 0.72' },
      { name: 'status', required: false, note: 'Default active (live Drivers list)' },
    ],
  },
];

function entityNoun(entity, count = 1) {
  if (entity === 'parties') return count === 1 ? 'party' : 'parties';
  if (entity === 'vehicles') return count === 1 ? 'vehicle' : 'vehicles';
  if (entity === 'drivers') return count === 1 ? 'driver' : 'drivers';
  return count === 1 ? 'load' : 'loads';
}

export default function ImportPage() {
  const qc = useQueryClient();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [jobId, setJobId] = useState('');
  const [dryErrors, setDryErrors] = useState([]);
  const [dryValid, setDryValid] = useState(null);
  const [dryPreview, setDryPreview] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [summary, setSummary] = useState('');
  const [fileName, setFileName] = useState('');
  const [entityType, setEntityType] = useState('loads');
  const [dateFormat, setDateFormat] = useState('');
  const [needsDateFormat, setNeedsDateFormat] = useState(false);
  const [busy, setBusy] = useState(false);

  const targetFields = ENTITY_FIELDS[entityType] ?? ENTITY_FIELDS.loads;
  const required = REQUIRED_FIELDS[entityType] ?? [];

  const historyQ = useQuery({
    queryKey: ['import-jobs'],
    queryFn: async () => {
      const r = await api('/api/v1/import/jobs');
      if (r.status !== 200) throw new Error(r.data?.error ?? 'Failed to load import history');
      return r.data.jobs ?? [];
    },
  });

  const history = historyQ.data ?? [];

  function applyParsed(hdrs, data, name, preferredEntity) {
    let detected = preferredEntity || detectEntityFromHeaders(hdrs);
    if (!ACTIVE_IMPORT_ENTITIES.has(detected)) {
      toast.message(`${detected} import is not supported — choose Loads, Parties, Vehicles, or Drivers`);
      detected = 'loads';
    }
    setEntityType(detected);
    setHeaders(hdrs);
    setRows(data);
    setFileName(name || '');
    setMapping(autoMapForEntity(detected, hdrs));
    setJobId('');
    setDryErrors([]);
    setDryValid(null);
    setDryPreview([]);
    setSummary('');
    setDateFormat('');
    setNeedsDateFormat(false);
    toast.success(
      `Parsed ${data.length} ${entityNoun(detected, data.length)} — map columns, then validate`
    );
  }

  function onFile(file) {
    if (!file) return;
    const name = file.name.toLowerCase();

    const finishMatrix = (matrix, fileLabel) => {
      if (!matrix?.length) {
        toast.error('Empty spreadsheet');
        return;
      }
      const rawHeaders = (matrix[0] || []).map((h) => String(h ?? ''));
      const hdrs = uniquifyHeaders(rawHeaders);
      const data = [];
      for (let r = 1; r < matrix.length; r++) {
        const arr = matrix[r] || [];
        const out = {};
        let any = false;
        hdrs.forEach((h, i) => {
          const v = cellToString(Array.isArray(arr) ? arr[i] : '');
          out[h] = v;
          if (v) any = true;
        });
        if (any) data.push(out);
      }
      if (!data.length) {
        toast.error('No data rows found');
        return;
      }

      let detected = detectEntityFromHeaders(hdrs);
      if (!ACTIVE_IMPORT_ENTITIES.has(detected)) detected = 'loads';

      const seen = new Set();
      const mapGuess = autoMapForEntity(detected, hdrs);
      const keyCol =
        detected === 'parties'
          ? mapGuess.name || hdrs.find((h) => /^name$/i.test(h)) || hdrs[0]
          : detected === 'vehicles'
            ? mapGuess.unitNumber || hdrs.find((h) => /unit|truck|vin/i.test(h)) || hdrs[0]
            : detected === 'drivers'
              ? mapGuess.name || hdrs.find((h) => /name|driver/i.test(h)) || hdrs[0]
              : mapGuess.loadNumber || hdrs.find((h) => /load/i.test(h)) || hdrs[0];
      const dedupeLabel =
        detected === 'parties' || detected === 'drivers'
          ? 'name'
          : detected === 'vehicles'
            ? 'unit #'
            : 'load #';
      const deduped = [];
      let dropped = 0;
      for (const row of data) {
        const key = String(row[keyCol] ?? '')
          .trim()
          .toLowerCase();
        if (key && seen.has(key)) {
          dropped += 1;
          continue;
        }
        if (key) seen.add(key);
        deduped.push(row);
      }
      if (dropped) {
        toast.message(`Removed ${dropped} duplicate ${dedupeLabel} row(s) from the file`);
      }
      applyParsed(hdrs, deduped, fileLabel, detected);
    };

    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      const reader = new FileReader();
      reader.onload = () => {
        const wb = XLSX.read(reader.result, { type: 'array', cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
        finishMatrix(matrix, file.name);
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    Papa.parse(file, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (result) => finishMatrix(result.data || [], file.name),
      error: () => toast.error('CSV parse failed'),
    });
  }

  const ready = headers.length > 0 && rows.length > 0;

  const mappedPreviewRows = useMemo(() => {
    return rows.slice(0, 20).map((row, idx) => {
      const values = {};
      for (const field of targetFields) {
        const col = mapping[field];
        values[field] = col ? String(row[col] ?? '') : '';
      }
      return { row: idx + 1, values };
    });
  }, [rows, mapping, targetFields]);

  async function ensureJobMapped() {
    if (!ACTIVE_IMPORT_ENTITIES.has(entityType)) {
      toast.error('Choose Loads, Parties, Vehicles, or Drivers to import');
      return null;
    }
    const missing = required.filter((f) => !mapping[f]);
    if (missing.length) {
      toast.error(`Map required fields: ${missing.join(', ')}`);
      return null;
    }

    let id = jobId;
    if (!id) {
      const create = await api('/api/v1/import/jobs', {
        method: 'POST',
        body: { entityType, headers, rows },
      });
      if (create.status !== 201) {
        toast.error(String(create.data?.error ?? 'Job create failed'));
        return null;
      }
      id = create.data.job.id;
      setJobId(id);
    }

    const body = { mapping };
    if (dateFormat) body.dateFormat = dateFormat;

    const map = await api(`/api/v1/import/jobs/${id}/mapping`, {
      method: 'POST',
      body,
    });
    if (map.status === 400 && map.data?.requiresDateFormatConfirmation) {
      setNeedsDateFormat(true);
      toast.error(String(map.data?.error ?? 'Confirm date format'));
      return null;
    }
    if (map.status !== 200) {
      toast.error(String(map.data?.error ?? 'Mapping failed'));
      return null;
    }
    setNeedsDateFormat(false);
    return id;
  }

  async function runDry() {
    if (busy) return;
    setBusy(true);
    try {
      const id = await ensureJobMapped();
      if (!id) return;
      const dry = await api(`/api/v1/import/jobs/${id}/dry-run`, { method: 'POST', body: {} });
      if (dry.status !== 200) {
        toast.error(String(dry.data?.error ?? 'Dry-run failed'));
        return;
      }
      setDryValid(dry.data.summary?.valid ?? 0);
      setDryErrors(dry.data.summary?.errors ?? []);
      setDryPreview(dry.data.summary?.preview ?? []);
      const errCount = dry.data.summary?.errors?.length ?? 0;
      toast.message(
        `Dry-run: ${dry.data.summary?.valid ?? 0} valid${errCount ? `, ${errCount} issue(s)` : ''}`
      );
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (busy) return;
    setBusy(true);
    try {
      const id = await ensureJobMapped();
      if (!id) return;
      if (dryValid === null) {
        toast.error('Run Validate / Preview first');
        return;
      }
      const commitRes = await api(`/api/v1/import/jobs/${id}/commit`, {
        method: 'POST',
        body: {},
      });
      if (commitRes.status !== 200) {
        toast.error(String(commitRes.data?.error ?? 'Commit failed'));
        return;
      }
      const n = commitRes.data.createdCount ?? 0;
      const skipped = commitRes.data.skipped?.length ?? 0;
      const label = entityNoun(entityType, n);
      setSummary(`Added ${n} ${label} to the system${skipped ? ` (${skipped} skipped)` : ''}`);
      toast.success(`Added ${n} ${label}`);
      qc.invalidateQueries({ queryKey: ['import-jobs'] });
      if (entityType === 'loads') {
        qc.invalidateQueries({ queryKey: ['loads'] });
        qc.invalidateQueries({ queryKey: ['shippers'] });
        qc.invalidateQueries({ queryKey: ['dispatch-board'] });
      }
      if (entityType === 'parties') {
        qc.invalidateQueries({ queryKey: ['shippers'] });
      }
      if (entityType === 'vehicles') {
        qc.invalidateQueries({ queryKey: ['fleet'] });
        qc.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      }
      if (entityType === 'drivers') {
        qc.invalidateQueries({ queryKey: ['drivers'] });
      }
    } finally {
      setBusy(false);
    }
  }

  const historyColumns = [
    {
      key: 'createdAt',
      label: 'Date',
      render: (v) => (v ? String(v).slice(0, 19) : '—'),
      className: 'font-mono text-xs',
    },
    { key: 'entityType', label: 'Entity' },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'id',
      label: 'Job',
      className: 'font-mono text-xs',
      render: (v) => String(v).slice(0, 8),
    },
  ];

  const confirmDescription =
    entityType === 'parties'
      ? 'Valid parties will be added as live Parties records. Existing names/emails/phones are never overwritten. Duplicate rows in the file are skipped.'
      : entityType === 'vehicles'
        ? 'Valid vehicles will be added to Fleet as active units (unless you map status to inactive/maintenance). Existing unit numbers and VINs are never overwritten. Duplicate rows in the file are skipped.'
        : entityType === 'drivers'
          ? 'Valid drivers will be added to the Drivers list as active (unless you map status to inactive). Existing names/emails/phones are never overwritten. Duplicate rows in the file are skipped.'
          : 'Valid historical loads will be added to Loads as closed/completed records (not live pending). Existing load numbers are never overwritten. Missing shippers/consignees are created automatically.';

  const previewSource = dryPreview.length ? dryPreview : mappedPreviewRows;
  const previewFields = targetFields.filter((f) =>
    previewSource.some((r) => r.values?.[f] || mapping[f])
  );

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Import"
        description="Import loads, parties, vehicles, and drivers from CSV/XLSX — one section at a time"
        icon="Upload"
        metrics={[
          { label: 'Entity', value: entityType },
          { label: 'Rows', value: rows.length },
          { label: 'Valid', value: dryValid ?? '—' },
          { label: 'Jobs', value: history.length },
        ]}
      />

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Download import templates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {TEMPLATES.map((t) => {
            const Icon = Icons[t.icon] || Icons.FileText;
            return (
              <div
                key={t.label}
                className={`rounded-xl border bg-card overflow-hidden ${t.active ? '' : 'opacity-60'}`}
              >
                <a
                  href={t.active ? t.file : undefined}
                  download={t.active ? true : undefined}
                  onClick={(e) => {
                    if (!t.active) {
                      e.preventDefault();
                      return;
                    }
                    setEntityType(t.entity);
                    setJobId('');
                    setDryValid(null);
                    setDryErrors([]);
                    setDryPreview([]);
                    setNeedsDateFormat(false);
                    setSummary('');
                    if (headers.length) {
                      setMapping(autoMapForEntity(t.entity, headers));
                    } else {
                      setMapping({});
                    }
                  }}
                  className="flex items-center justify-between gap-3 p-4 hover:bg-muted/40 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {t.entity === 'loads'
                          ? 'Download .csv — historical loads ready to import'
                          : t.entity === 'parties'
                            ? 'Download .csv — live parties ready to import'
                            : t.entity === 'vehicles'
                              ? 'Download .csv — active fleet vehicles ready to import'
                              : 'Download .csv — active drivers ready to import'}
                      </p>
                    </div>
                  </div>
                  <Icons.Download className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </a>
                <div className="border-t px-4 py-3 space-y-1">
                  <p className="text-[10px] uppercase font-medium tracking-wider text-muted-foreground mb-2">
                    Columns
                  </p>
                  {t.fields.map((f) => (
                    <div key={f.name} className="flex items-start gap-2 text-xs">
                      <span
                        className={`font-mono ${f.required ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}
                      >
                        {f.name}
                        {f.required ? ' *' : ''}
                      </span>
                      {f.note && <span className="text-muted-foreground">— {f.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          * = required. Import <span className="font-medium">Loads</span>,{' '}
          <span className="font-medium">Parties</span>, <span className="font-medium">Vehicles</span>,
          or <span className="font-medium">Drivers</span>. Parties, vehicles, and drivers land as
          live/active records; loads default to historical closed. Duplicate columns and keys are
          handled safely.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload File</CardTitle>
          <CardDescription>CSV or XLSX — columns are auto-mapped, then you can adjust</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
            <Icons.UploadCloud className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium">Drop file here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">CSV, XLSX up to 10MB · max 2000 rows</p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
              className="hidden"
              id="file-upload"
            />
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              Select File
            </Button>
            {fileName && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm">
                <Icons.FileText className="h-4 w-4" />
                <span className="font-mono">{fileName}</span>
                <span className="text-xs text-muted-foreground">
                  ({rows.length} rows · {entityType})
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {ready && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Column Mapping</CardTitle>
              <CardDescription>
                Importing as{' '}
                <select
                  value={entityType}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (!ACTIVE_IMPORT_ENTITIES.has(next)) {
                      toast.message(`${next} import is not supported`);
                      return;
                    }
                    setEntityType(next);
                    setMapping(autoMapForEntity(next, headers));
                    setJobId('');
                    setDryValid(null);
                    setDryErrors([]);
                    setDryPreview([]);
                    setNeedsDateFormat(false);
                    setSummary('');
                  }}
                  className="ml-1 px-2 py-1 border rounded-md text-sm bg-background"
                >
                  <option value="loads">loads (historical records)</option>
                  <option value="parties">parties (live)</option>
                  <option value="vehicles">vehicles (active fleet)</option>
                  <option value="drivers">drivers (active)</option>
                </select>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {targetFields.map((field) => (
                <div key={field} className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {field}
                    {required.includes(field) ? ' *' : ''}
                  </label>
                  <select
                    value={mapping[field] ?? ''}
                    onChange={(e) => {
                      setMapping((m) => ({ ...m, [field]: e.target.value }));
                      setDryValid(null);
                      setDryErrors([]);
                      setDryPreview([]);
                      setSummary('');
                    }}
                    className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="">—</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

              {(needsDateFormat || entityType === 'loads') && (
                <div className="space-y-1 pt-1">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date format {needsDateFormat ? '*' : '(optional)'}
                  </label>
                  <select
                    value={dateFormat}
                    onChange={(e) => {
                      setDateFormat(e.target.value);
                      setDryValid(null);
                    }}
                    className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                  >
                    <option value="">Auto-detect</option>
                    <option value="ISO">YYYY-MM-DD (ISO)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  </select>
                  {needsDateFormat ? (
                    <p className="text-xs text-amber-700">
                      Dates are ambiguous — choose MM/DD or DD/MM, then validate again.
                    </p>
                  ) : null}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={() => void runDry()} disabled={busy}>
                  {busy ? (
                    <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Icons.ListChecks className="h-4 w-4 mr-2" />
                  )}
                  Validate / Preview
                </Button>
                <Button
                  disabled={busy || dryValid === null || dryValid < 1}
                  onClick={() => setShowConfirm(true)}
                >
                  <Icons.Database className="h-4 w-4 mr-2" />
                  Add to system
                </Button>
              </div>
              {jobId ? <p className="text-xs text-muted-foreground font-mono">Job {jobId}</p> : null}
              {dryValid !== null ? (
                <p className="text-sm">
                  <span className="font-medium text-success">{dryValid}</span> ready to add
                  {dryErrors.length ? (
                    <>
                      {' '}
                      · <span className="text-destructive">{dryErrors.length}</span> field issue(s)
                    </>
                  ) : null}
                </p>
              ) : null}
              {summary ? <p className="text-sm font-medium">{summary}</p> : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                {dryErrors.length && dryPreview.length === 0 ? 'Field Errors' : 'Mapped Preview'}
              </CardTitle>
              <CardDescription>
                {dryPreview.length
                  ? 'Validated mapped fields (first 25 rows)'
                  : `Mapped columns for first ${mappedPreviewRows.length} of ${rows.length} rows`}
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-auto max-h-[32rem]">
              {previewFields.length === 0 ? (
                <p className="text-sm text-muted-foreground">Map columns to see a preview</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-2">#</th>
                      {dryPreview.length ? <th className="py-1 pr-2">Status</th> : null}
                      {previewFields.map((h) => (
                        <th key={h} className="py-1 pr-2 whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewSource.map((r) => (
                      <tr key={r.row} className="border-t align-top">
                        <td className="py-1 pr-2 font-mono text-xs">{r.row}</td>
                        {dryPreview.length ? (
                          <td className="py-1 pr-2">
                            <StatusPill status={r.status === 'valid' ? 'ready' : 'error'} />
                            {r.message ? (
                              <p className="text-[11px] text-muted-foreground mt-0.5 max-w-[10rem]">
                                {r.message}
                              </p>
                            ) : null}
                          </td>
                        ) : null}
                        {previewFields.map((h) => (
                          <td key={h} className="py-1 pr-2 max-w-[9rem] truncate" title={r.values?.[h]}>
                            {r.values?.[h] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {dryErrors.length > 0 ? (
                <div className="mt-4 border-t pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Field errors
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-1">Row</th>
                        <th>Field</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryErrors.slice(0, 40).map((err, i) => (
                        <tr key={`${err.row}-${err.field}-${i}`} className="border-t">
                          <td className="py-1 font-mono">{err.row}</td>
                          <td className="font-mono">{err.field ?? '—'}</td>
                          <td className="text-destructive">{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Import History</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={historyColumns}
            data={history}
            emptyMessage={historyQ.isLoading ? 'Loading…' : 'No import history yet'}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title={`Add ${dryValid ?? 0} ${entityNoun(entityType, dryValid ?? 0)} to the system?`}
        description={confirmDescription}
        confirmLabel="Add to system"
        onConfirm={commit}
      />
    </div>
  );
}
